import { FormProvider } from "react-hook-form";
import React, { createContext, useContext, useMemo, useState } from "react";
import { useEntrepotFormLogic } from "../hooks/useEntrepotFormLogic";

type EntrepotFormContextType = {
  currentParentCompany: any;
  currentWarehouse: any;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  entrepotError?: any;
  setEntrepotError: (error: any) => void;
  entrepot?: any;
  defaultValuesForm?: any;
  parentCompanyId?: string;
  handleUserAddClick: () => void;
  handleUserPopinSubmit: (data: any) => void;
  openUser: boolean;
  toggleUserPopin: (isOpen: boolean) => void;
  onAddClick: () => void;
  setTitle: (title: string) => void;
  onUserSubmit: (data: any) => Promise<any>;
  onUserSubmitEdit: (data: any) => Promise<any>;
  errorUser: string;
  matchingWarehouses: any[];
  fuzzySearchOnWarehouseName: (input: string) => void;
};

export const EntrepotFormContext = createContext<
  EntrepotFormContextType | undefined
>(undefined);

export const useEntrepotForm = () => {
  const context = useContext(EntrepotFormContext);
  if (context === undefined) {
    throw new Error("useEntrepotForm must be used within EntrepotFormProvider");
  }
  return context;
};

type EntrepotFormProviderProps = {
  children: React.ReactNode;
  entrepot?: any;
  entrepotError?: any;
  defaultValuesForm?: any;
  isSubmitting?: boolean;
  parentCompanyId?: string;
};

export const EntrepotFormProvider: React.FC<EntrepotFormProviderProps> = ({
  children,
  entrepot,
  entrepotError: initialEntrepotError,
  isSubmitting: initialIsSubmitting = false,
  defaultValuesForm,
  parentCompanyId,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(initialIsSubmitting);
  const [entrepotError, setEntrepotError] = useState(initialEntrepotError);

  const {
    form,
    currentParentCompany,
    currentWarehouse,
    handleUserAddClick,
    handleUserPopinSubmit,
    // user form
    onAddClick,
    openUser,
    toggleUserPopin,
    onUserSubmit,
    onUserSubmitEdit,
    errorUser,
    setTitle,
    matchingWarehouses,
    fuzzySearchOnWarehouseName,
  } = useEntrepotFormLogic(entrepot, parentCompanyId);

  const value = useMemo(
    () => ({
      currentParentCompany,
      currentWarehouse,
      isSubmitting,
      setIsSubmitting,
      entrepotError,
      setEntrepotError,
      entrepot,
      defaultValuesForm,
      parentCompanyId,
      handleUserAddClick,
      handleUserPopinSubmit,
      /* user form */
      onAddClick,
      openUser,
      toggleUserPopin,
      onUserSubmit,
      onUserSubmitEdit,
      errorUser,
      setTitle,
      matchingWarehouses,
      fuzzySearchOnWarehouseName,
    }),
    [
      currentParentCompany,
      currentWarehouse,
      isSubmitting,
      entrepotError,
      entrepot,
      defaultValuesForm,
      parentCompanyId,
      handleUserAddClick,
      handleUserPopinSubmit,
      onAddClick,
      openUser,
      toggleUserPopin,
      onUserSubmit,
      onUserSubmitEdit,
      errorUser,
      setTitle,
      matchingWarehouses,
      fuzzySearchOnWarehouseName,
    ]
  );

  return (
    <EntrepotFormContext.Provider value={value}>
      <FormProvider {...form}>{children}</FormProvider>
    </EntrepotFormContext.Provider>
  );
};
